import { forwardRef, useCallback, useEffect, useImperativeHandle, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Mic, Send, Square } from 'lucide-react'
import { Button, Textarea } from '@/components/ui'
import { useFeedbackVoice } from '@/hooks/useFeedbackVoice'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import { cn } from '@/utils'
import { usePanelControls } from './helpFeedbackContext'
import {
  COMPOSER_HINT, LISTENING, MESSAGE_LABEL, MIC_BLOCKED, MIC_BLOCKED_HOW, MIC_CONTINUE, MIC_EXPLAIN, MIC_LABEL,
  MIC_NOT_NOW, MIC_STOP, PLACEHOLDER, PLACEHOLDER_MORE, SEND_MESSAGE,
} from './helpFeedbackText'

export const MIC_EXPLAINED_KEY = 'help-feedback:mic-explained'

function micExplained(): boolean {
  try {
    return window.localStorage.getItem(MIC_EXPLAINED_KEY) === '1'
  } catch {
    return false
  }
}

function rememberMicExplained() {
  try {
    window.localStorage.setItem(MIC_EXPLAINED_KEY, '1')
  } catch {
    // A private window keeps no memory; the explanation shows again next time.
  }
}

export interface ComposerHandle {
  focus: () => void
}

interface Props {
  /** True while the person is adding to a summary. */
  addingMore: boolean
  disabled: boolean
  /** True while a turn runs: a send is held until it ends. */
  busy: boolean
  /** The person's words as a turn; answers false when it could not go yet. */
  onSubmit: (text: string, spoken: boolean) => boolean
}

/**
 * Where the person types or talks (decisions 8, 15). Enter sends, Shift+Enter breaks a line. Words sent
 * while a turn runs stay in the box and go once it ends. The mic explains itself once, listens until a
 * pause, and puts the words in the box to edit; it never sends on its own.
 */
export const Composer = forwardRef<ComposerHandle, Props>(function Composer({ addingMore, disabled, busy, onSubmit }, ref) {
  const id = useId()
  const text = useHelpFeedbackStore((s) => s.draftText)
  const spoken = useHelpFeedbackStore((s) => s.draftSpoken)
  const setDraft = useHelpFeedbackStore((s) => s.setDraft)
  const micHidden = useHelpFeedbackStore((s) => s.micHidden)
  const voiceOn = useHelpFeedbackStore((s) => s.voice)
  const { escape } = usePanelControls()
  const box = useRef<HTMLTextAreaElement>(null)
  const [held, setHeld] = useState(false)
  const [explaining, setExplaining] = useState(false)

  useImperativeHandle(ref, () => ({ focus: () => box.current?.focus() }), [])

  const onFinal = useCallback((words: string) => {
    const said = words.trim()
    if (!said) return
    const now = useHelpFeedbackStore.getState().draftText
    setDraft(now.trim() ? `${now.trimEnd()} ${said}` : said, true)
    box.current?.focus()
  }, [setDraft])

  const voice = useFeedbackVoice({ onFinal })
  const showMic = voice.engine !== 'none' && !micHidden && voiceOn

  // Esc stops the mic first, and only then closes the panel.
  useEffect(() => {
    if (!voice.listening) return
    const stop = voice.stop
    escape.current = () => {
      stop()
      return true
    }
    return () => {
      escape.current = null
    }
  }, [voice.listening, voice.stop, escape])

  // A refused microphone hides the mic for the session and says why, once.
  const denied = voice.denied
  useEffect(() => {
    if (!denied) return
    const store = useHelpFeedbackStore.getState()
    if (store.micHidden) return
    store.hideMic()
    store.dispatch({ type: 'say', text: MIC_BLOCKED })
    store.dispatch({ type: 'say', text: MIC_BLOCKED_HOW })
  }, [denied])

  const submit = useCallback(() => {
    const words = useHelpFeedbackStore.getState().draftText
    if (!words.trim() || disabled) return
    if (busy) {
      setHeld(true)
      return
    }
    if (onSubmit(words, useHelpFeedbackStore.getState().draftSpoken)) {
      setDraft('')
      setHeld(false)
    }
  }, [busy, disabled, onSubmit, setDraft])

  // A send held during a turn goes once the turn ends.
  useEffect(() => {
    if (held && !busy) submit()
  }, [held, busy, submit])

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
    e.preventDefault()
    submit()
  }

  const onMic = () => {
    if (voice.listening) {
      voice.stop()
      return
    }
    if (!micExplained()) {
      setExplaining(true)
      return
    }
    voice.start()
  }

  return (
    <div className="space-y-2">
      {explaining && (
        <div className="space-y-2 rounded-lg border border-section-border bg-card p-3 text-sm shadow-xs">
          <p>{MIC_EXPLAIN}</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                rememberMicExplained()
                setExplaining(false)
                voice.start()
              }}
            >
              {MIC_CONTINUE}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setExplaining(false)}>{MIC_NOT_NOW}</Button>
          </div>
        </div>
      )}
      {voice.listening && (
        <div className="text-xs text-muted-foreground" role="status">
          <p>{LISTENING}</p>
          {voice.partial && <p className="mt-1 text-sm text-foreground italic">{voice.partial}</p>}
        </div>
      )}
      <div className="flex items-end gap-2">
        <label htmlFor={id} className="sr-only">{MESSAGE_LABEL}</label>
        <Textarea
          id={id}
          ref={box}
          rows={2}
          value={text}
          disabled={disabled}
          placeholder={addingMore ? PLACEHOLDER_MORE : PLACEHOLDER}
          onChange={(e) => setDraft(e.target.value, e.target.value ? spoken : false)}
          onKeyDown={onKeyDown}
          className="max-h-40 min-h-10 flex-1 resize-none text-sm"
        />
        {showMic && (
          <Button
            type="button"
            size="sm"
            variant={voice.listening ? 'secondary' : 'ghost'}
            aria-label={voice.listening ? MIC_STOP : MIC_LABEL}
            aria-pressed={voice.listening}
            disabled={disabled}
            onClick={onMic}
            className={cn('size-9 px-0', voice.listening && 'text-primary')}
          >
            {voice.listening ? <Square aria-hidden /> : <Mic aria-hidden />}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          aria-label={SEND_MESSAGE}
          disabled={disabled || !text.trim()}
          isLoading={held}
          onClick={submit}
          className="size-9 px-0"
        >
          <Send aria-hidden />
        </Button>
      </div>
      <p className="hidden text-xs text-muted-foreground sm:block">{COMPOSER_HINT}</p>
    </div>
  )
})
